import React, { useState, useEffect } from "react";
import axios from "axios";
import { SERVER_URL } from "../../config";
import ChatBox from "./ChatBox";
import MessageBox from "./MessageBox";
import "../../styles/messages.css";
import useCurrentProvider from "../../hooks/useCurrentProvider";

interface Patient {
  id: string;
  firstName: string;
  email: string;
  address: string;
  region: string;
  city: string;
  dob: string;
  sentMessages: string;
  userId: string;
}

interface MessagesModalProps {
  patientProfile: Patient;
  onClose: () => void;
}

/**
 * MessageModal
 * - Fetches/creates a chat with the given patient's userId.
 * - Prevents self-chats by comparing if the patient's userId matches the auth user's ID.
 * - If chat is found or created, displays ChatBox and MessageBox.
 */
const MessageModal: React.FC<MessagesModalProps> = ({
  patientProfile,
  onClose,
}) => {
  const [chatId, setChatId] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [reloadTrigger, setReloadTrigger] = useState<number>(0);

  const token = localStorage.getItem("token");
  const patientUserId = patientProfile.userId;

  const user = useCurrentProvider();
  const myUserId = user.user?.id;

  useEffect(() => {
    const fetchOrCreateChat = async () => {
      try {
        // 1. Prevent self-chats if the patient's userId equals our own userId
        if (myUserId && myUserId === patientUserId) {
          setError("Cannot create a chat with yourself.");
          return;
        }

        // 2. Attempt to find an existing chat with the patient's userId
        const getResponse = await axios.get(
          `${SERVER_URL}/api/message/chats?otherUserId=${patientUserId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // 3. If a chat is found, use that chatId
        if (getResponse.data && getResponse.data.id) {
          console.log("Chat found:", getResponse.data);
          setChatId(getResponse.data.id);
        } else {
          // 4. Otherwise, create a new chat (unless there's an error)
          const createResponse = await axios.post(
            `${SERVER_URL}/api/message/chats`,
            { otherUserId: patientUserId },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          console.log("Chat created:", createResponse.data);
          setChatId(createResponse.data.id);
        }
      } catch (err: any) {
        console.error("Error fetching/creating chat:", err);
        setError("Failed to fetch or create chat.");
      }
    };

    fetchOrCreateChat();
  }, [patientUserId, token, myUserId]);

  // Called by MessageBox after a new message is sent
  const handleNewMessageSent = () => {
    setReloadTrigger((prev) => prev + 1); // Force ChatBox to reload
  };

  const handleClose = () => {
    console.log("Close button clicked");
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Chat with {patientProfile.firstName}</h2>
        {error && <p className="error-message">{error}</p>}

        {/* If there's an error, don't render the chat UI */}
        {!error && chatId && myUserId ? (
          <>
            <ChatBox
              chatId={chatId}
              reloadTrigger={reloadTrigger}
              myUserId={myUserId}
            />
            <MessageBox chatId={chatId} onMessageSent={handleNewMessageSent} />
          </>
        ) : !error ? (
          <p>Loading chat...</p>
        ) : null}

        <button className="close-button" onClick={handleClose}>
          X
        </button>
      </div>
    </div>
  );
};

export default MessageModal;
